import React, { useEffect, useState } from "react";
import { Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import CommonTitleBar from "../../../components/common/CommonTitleBar";
import MeetingRoomsCard from "./MeetingRoomsCard";
import NoDataFound from "components/common/NoDataFound";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllWorkspaces,
  getEnableDisableSpaceStatus,
  updateEnableDisableSpaceStatus,
} from "store/actions/workspaceSlice";
import { showAlert } from "store/reducers/alertReducer";
import Paginations from "components/common/Paginations";
import Spinners from "components/common/Spinners";
import SpaceShimmer from "components/ShimmerEffects/SpaceShimmer";
import { InputSwitch } from "primereact/inputswitch";
import eye from "assets/images/icons/eye.svg";
import orderChange from "assets/images/icons/orderChange.svg";
import { syncLocks } from "store/actions/branchSlice";

const MeetingRooms = () => {
  const { t } = useTranslation();
  document.title = t("Meeting Rooms | Ashjar Space");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const pageNumber = Number(new URLSearchParams(search).get("page")) || 1;

  const {
    isLoading,
    loading,
    workspaces: meetingRooms,
    drafts,
    perPage,
    totalCount,
    totalPages,
    currentPage,
    spaceStatus,
    currentSpaceStatus,
  } = useSelector((state) => state.Workspace);

  const matchedSpace = currentSpaceStatus?.find(
    (space) => space.name === "meetingRoom"
  );
  const [displayOrder, setDisplayOrder] = useState(null);

  useEffect(() => {
    if (matchedSpace) {
      setDisplayOrder(matchedSpace.displayOrder);
    }
  }, [matchedSpace]);

  useEffect(() => {
    getAllData();
  }, []);

  const getAllData = (page) => {
    const payload = {
      spaceTypeInt: 2, // Space Type Int [1-workSpace , 2-meetingRoom , 3-workShop]
      perPage: perPage,
      pageNo: page || pageNumber,
      status: -1,
      orderBy: -1,
      keyword: "",
      dateRange: {},
    };
    dispatch(getAllWorkspaces(payload));
    dispatch(getEnableDisableSpaceStatus());
    navigate(`/bookings/meeting-rooms?page=${page || pageNumber}`);
  };

  const updateSpaceStatus = (id, isHide, newOrder) => {
    const payload = {
      id,
      displayOrder: newOrder,
      isHide,
    };
    dispatch(updateEnableDisableSpaceStatus(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(
            showAlert({ text: "Status updated Successfully!", type: "success" })
          );
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const handleSyncLocks = () => {
    dispatch(syncLocks("meetingRoom"))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  return (
    <React.Fragment>
      <div className="page-content meeting_rooms_page common_page">
        <Container fluid>
          <CommonTitleBar
            title={t("Meeting Rooms")}
            isCreateBtn={true}
            createBtnText={t("Add New")}
            onCreateBtnClick={() => navigate("/bookings/meeting-rooms/add-new")}
            // isCreateBtn2={true}
            // createBtnText2={t("Sync Locks")}
            // onCreateBtnClick2={handleSyncLocks}
            isLoading={isLoading}
          />
          <div className="common_space_app_settings row">
            <div className="col-lg-6">
              <label htmlFor="">
                <img src={eye} width={15} height={15} className="me-2" alt="" />
                {t("Show in App")}
              </label>
              <div className="checkbox_div">
                <InputSwitch
                  checked={!matchedSpace?.isHide ?? false} // use matchedSpace
                  onChange={() => {
                    if (!matchedSpace) return;

                    // Toggle isHide and send current displayOrder
                    updateSpaceStatus(
                      matchedSpace._id,
                      !matchedSpace.isHide,
                      matchedSpace.displayOrder
                    );
                  }}
                />
                <h2 className="fs_16 ms-2">
                  {t(
                    "Turn on to display the “Meeting Rooms” page in the app. Turn off to hide it"
                  )}
                </h2>
              </div>
            </div>

            <div className="col-lg-6">
              <label htmlFor="">
                <img
                  src={orderChange}
                  width={15}
                  height={15}
                  className="me-2"
                  alt=""
                />
                {t("Display Order in App")}
              </label>
              <div className="">
                <select
                  value={displayOrder} // use state
                  onChange={(e) => {
                    const newOrder = Number(e.target.value);
                    setDisplayOrder(newOrder);
                    if (!matchedSpace) return;

                    updateSpaceStatus(
                      matchedSpace._id,
                      matchedSpace.isHide,
                      newOrder
                    );
                  }}
                >
                  {[1, 2, 3, 4, 5].map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
                <p className="short_desc">
                  {t(
                    "Choose the position of this page in the app menu. 1 shows first, higher numbers appear later"
                  )}
                </p>
              </div>
            </div>
          </div>
          <div className="common_wrapper">
            <Row>
              {loading ? (
                <SpaceShimmer col={4} />
              ) : meetingRooms?.length === 0 ? (
                <NoDataFound text={t("No meeting rooms found")}/>
              ) : (
                meetingRooms?.map((item) => (
                  <MeetingRoomsCard
                    key={item?.id}
                    item={item}
                    isLoading={isLoading}
                  />
                ))
              )}
            </Row>
          </div>
          <h2 className="main_title">{t("Draft")}</h2>
          <div className="common_wrapper">
            {loading ? (
              <SpaceShimmer col={4} />
            ) : drafts?.length === 0 ? (
              <NoDataFound text={t("No drafts saved yet")} />
            ) : (
              <Row>
                {drafts?.map((item) => (
                  <MeetingRoomsCard
                    key={item?.id}
                    item={item}
                    className="draft"
                    isLoading={isLoading}
                  />
                ))}
              </Row>
            )}
          </div>
          {!loading && (
            <Paginations
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                getAllData(page);
              }}
              totalCount={totalCount}
            />
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default MeetingRooms;
